import { Box, Grid, TextField, Typography } from '@mui/material';
import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import styles from '../../../assets/styles/components/Forms/Form.module.scss';
import { XsOnly } from '../../../utils/breakpoints';
import PageHeader from '../../PageHeader';
import {
  Brand,
  CampaignAd,
  ChangeEventType,
  FBCarouselTemplate,
  FbAssetFeedSpec,
  FbCreativeForm,
} from '../../../types';
import { useDispatch } from 'react-redux';
import { PrimaryButton } from '../../Buttons';
import 'react-multi-carousel/lib/styles.css';
import LinearProgressBarWithLabel from '../../ProgressBar/LinearProgressBarWithLabel';
import { AuthContext } from '../../../context';
import { ADMIN, CUSTOM_FIELD_REGEX, SUPER_ADMIN } from '../../../utils';
import {
  createCampaignTemplate,
  fetchSingleCampaignTemplate,
  getSingleDiySetting,
  updateCampaignTemplate,
} from '../../../services/diy';
import { AdsTemplate, DiySettings } from '../../../types/IDiy';
import CreativeForm from '../Ads/CreativeForm';
import { CallToActionType } from '../../../utils/constants/facebookAds';
import { toggleAlert } from '../../../redux/actions';
import { BusinessType } from '../../../utils/helpers/BrandHelpers';
import Spinner from '../../Spinner';
import CircularLoading from '../../CircularLoading';
import { getImproveAdByTemplate } from '../../../services/ai';
import { useForm } from 'react-hook-form';
import PopupModal from '../../Modal';
import ModalHeader from '../../Modal/ModalHeader';

const STEPS = ['Creative', 'Details'];

interface TemplateFormProps {
  brand: Brand;
  isEdit?: boolean;
  isDuplicate?: boolean;
  templateId?: string;
  templates: AdsTemplate[];
  setTemplates: Dispatch<SetStateAction<AdsTemplate[]>>;
  onClose: () => void;
  isCreateAds?: boolean;
  setFilteredTemplates?: Dispatch<SetStateAction<AdsTemplate[]>>;
  onSelectTemplate?: (template: AdsTemplate) => void;
}

const TemplateForm: React.FC<TemplateFormProps> = ({
  brand,
  isEdit = false,
  isDuplicate = false,
  templates,
  templateId = null,
  setTemplates = null,
  onClose,
  isCreateAds = false,
  setFilteredTemplates = null,
  onSelectTemplate = null,
}) => {
  const {
    formState: { errors },
    register,
    handleSubmit,
    clearErrors,
    setValue,
  } = useForm({ mode: 'onSubmit' });
  const dispatch = useDispatch();

  const { state } = useContext(AuthContext);
  const isSuperAdmin = state.role === SUPER_ADMIN;
  const isAdmin = state.role === ADMIN;
  const [formValues, setFormValues] = useState<AdsTemplate>({
    name: '',
    description: '',
    archived: false,
    category:
      isEdit || isDuplicate
        ? ''
        : (brand?.__type === 'location' ? brand?.franchisor : brand)
            ?.businessType === BusinessType.FRANDEV
        ? 'Promotions'
        : '',
    media: '',
    mediaEditable: false,
    aiAnalyze: {
      adCopy: '',
      analysis: '',
      grade: '',
      generatedAdCopy: '',
      archived: false,
      graded: false,
    },
    usageCount: 0,
    adsUsage: [],
  });
  const [creativeTemplate, setCreativeTemplate] = useState<FbCreativeForm>({
    name: '',
    object_story_spec: {
      page_id: '',
    },
  });
  const [carouselData, setCarouselData] = useState<FBCarouselTemplate[]>([]);
  const [format, setFormat] = useState<string>(null);
  const [mediaType, setMediaType] = useState<string>(null);
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0);
  const [progress, setProgress] = useState<number>((1 / STEPS.length) * 100);
  const [activeStep, setActiveStep] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [mediaForm, setMediaForm] = useState<FbAssetFeedSpec>({
    bodies: [],
    call_to_action_types: [CallToActionType.LEARN_MORE],
    call_to_actions: [
      {
        type: CallToActionType.LEARN_MORE,
        adlabels: [{ name: 'cta_1' }],
      },
    ],
    titles: [],
    descriptions: [],
    link_urls: [],
    ad_formats: [],
    asset_customization_rules: [
      // SQUARE
      {
        customization_spec: {
          // TODO: Add IG
          publisher_platforms: ['facebook', 'instagram'],
          facebook_positions: ['feed', 'video_feeds', 'marketplace', 'search'],
          instagram_positions: [
            'stream',
            'ig_search',
            'profile_feed',
            'explore',
            'explore_home',
          ],
        },
      },
      // Vertical
      {
        customization_spec: {
          // TODO: Add IG
          publisher_platforms: [
            'facebook',
            'instagram',
            'messenger',
            'audience_network',
          ],
          facebook_positions: ['story', 'facebook_reels'],
          instagram_positions: ['reels', 'story'],
          messenger_positions: ['story'],
        },
      },
      {
        customization_spec: {
          publisher_platforms: ['facebook'],
          facebook_positions: ['right_hand_column'],
        },
      },
    ],
  });
  const [diySettings, setDiySettings] = useState<DiySettings>(null);
  const [confirmEdit, setConfirmEdit] = useState<boolean>(false);

  useEffect(() => {
    if (brand) getDiySettings(brand);
  }, [brand]);

  useEffect(() => {
    if (templateId && (isEdit || isDuplicate)) {
      getSingleTemplate();
    }
  }, [templateId, isEdit, isDuplicate]);

  useEffect(() => {
    if (STEPS.length > 0) {
      const step = STEPS.find((value: string, index: number) => {
        return index === activeStepIndex;
      });

      setActiveStep(step);
    }
  }, [STEPS, activeStepIndex]);

  useEffect(() => {
    if ((isEdit || isDuplicate) && formValues) {
      if (formValues?.media === 'carousel') {
        setFormat('carousel');
      } else {
        if (formValues?.media === 'image') {
          setFormat('media');
          setMediaType('image');
        } else {
          setFormat('media');
          setMediaType('video');
        }
      }
    }
  }, [isEdit, formValues, isDuplicate]);

  const getSingleTemplate = async () => {
    try {
      setLoading(true);

      let temp: AdsTemplate = { ...formValues };
      const response = await fetchSingleCampaignTemplate(templateId);

      temp = {
        ...temp,
        ...response.data,
        name: isDuplicate
          ? `Copy of ${response.data.name}`
          : response.data.name,
      };

      const improveAd = await getImproveAdByTemplate(
        brand?._id,
        response.data._id,
      );

      if (improveAd.data) {
        temp = {
          ...temp,
          aiAnalyze: {
            ...temp?.aiAnalyze,
            ...improveAd.data,
          },
        };
      }

      setFormValues(temp);
      setMediaForm({ ...response.data.creativeTemplate.asset_feed_spec });
      setCreativeTemplate({ ...response.data.creativeTemplate });
      setCarouselData([
        ...(response.data.creativeTemplate?.object_story_spec?.link_data
          ?.child_attachments || []),
      ]);
      setValue('description', temp?.description);
      setValue('name', temp?.name);
      setValue(
        'body',
        temp?.creativeTemplate?.asset_feed_spec?.bodies[0]?.text,
      );
      setValue(
        'title',
        temp?.creativeTemplate?.asset_feed_spec?.titles[0]?.text,
      );
    } catch (error: any) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const getDiySettings = async (brand: Brand) => {
    try {
      setLoading(true);
      const response = await getSingleDiySetting(brand?._id);

      setDiySettings(response.data);
    } catch (error: any) {
      console.log(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOnSubmit = async () => {
    try {
      if (formValues?.usageCount > 0 && !confirmEdit && isEdit) {
        return setConfirmEdit(true);
      }

      setLoading(true);

      let assetFeedSpecTemp: FbAssetFeedSpec = { ...mediaForm };

      if (!assetFeedSpecTemp?.descriptions[0]?.text) {
        assetFeedSpecTemp = {
          ...assetFeedSpecTemp,
          descriptions: [{ text: ' ' }],
        };
      }

      assetFeedSpecTemp = {
        ...assetFeedSpecTemp,
        call_to_actions: [
          {
            ...assetFeedSpecTemp?.call_to_actions[0],
            value: {
              ...assetFeedSpecTemp?.call_to_actions[0]?.value,
              lead_gen_form_id: null,
              link: null,
            },
          },
        ],
        link_urls: [
          {
            // TODO: Check this if not required to pass to the Asset Feed Spec API
            website_url: diySettings?.defaultUrl,
            display_url: diySettings?.defaultUrl,
          },
        ],
      };

      let tempCreativeTemplate: FbCreativeForm = {
        ...creativeTemplate,
        asset_feed_spec: { ...assetFeedSpecTemp },
        object_story_spec: {
          ...creativeTemplate.object_story_spec,
          page_id: diySettings?.facebookPage?.fbPageId,
        },
        contextual_multi_ads: {
          enroll_status: 'OPT_OUT',
        },
      };

      if (diySettings?.instagramAccount?.instagramId) {
        tempCreativeTemplate = {
          ...tempCreativeTemplate,
          object_story_spec: {
            ...tempCreativeTemplate.object_story_spec,
            instagram_actor_id: diySettings?.instagramAccount?.instagramId,
          },
        };
      }

      const matches =
        tempCreativeTemplate?.asset_feed_spec?.bodies[0]?.text.match(
          CUSTOM_FIELD_REGEX,
        );

      let customFields: any[] = [];

      if (matches?.length > 0) {
        matches.forEach((customField: string) => {
          const params: any = { field: customField, value: '' };

          customFields = [...customFields, params];
        });
      }

      let params: AdsTemplate = {
        ...formValues,
        ref: (brand?.__type === 'location' ? brand?.franchisor : brand)?._id,
        docModel: 'Brand',
        creativeTemplate: tempCreativeTemplate,
        media:
          format === 'carousel'
            ? 'carousel'
            : mediaType === 'image'
            ? 'image'
            : 'video',
        accountId: diySettings?.fbAdAccount?.accountId,
        _id: isDuplicate ? null : formValues?._id,
        customFields,
      };

      if (isEdit) {
        const response = await updateCampaignTemplate(templateId, params);
        const data: AdsTemplate = response.data;

        let temp: AdsTemplate[] = [];
        templates.forEach((template: AdsTemplate) => {
          if (template._id === data._id) {
            template = data;
            template.usageCount = formValues?.usageCount || 0;
            template.adsUsage = formValues?.adsUsage || [];
          }

          temp = [...temp, template];
        });

        setTemplates(temp);
        if (isCreateAds) {
          setFilteredTemplates(temp);
          onSelectTemplate(data);
        }
      } else {
        const response = await createCampaignTemplate(
          (brand?.__type === 'location' ? brand?.franchisor : brand)?._id,
          params,
        );

        const data: AdsTemplate = response.data;

        setTemplates((prev) => [data, ...prev]);
        if (isCreateAds) {
          setFilteredTemplates((prev) => [data, ...prev]);
          onSelectTemplate(data);
        }
      }

      dispatch(
        toggleAlert({
          toggle: true,
          message: isEdit
            ? 'Ads template updated successfully'
            : isDuplicate
            ? 'Ads template duplicated successfully'
            : 'Ads template created successfully',
        }),
      );

      if (confirmEdit) handleCloseConfirmEdit();
      onClose();
    } catch (error: any) {
      console.log(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 'Creative' && !mediaType) {
      return dispatch(
        toggleAlert({
          toggle: true,
          message: 'Please select one (1) of the media type',
          type: 'error',
        }),
      );
    } else if (
      activeStep === 'Creative' &&
      mediaType === 'image' &&
      (!formValues?.images?.square || !formValues?.images?.vertical)
    ) {
      return dispatch(
        toggleAlert({
          toggle: true,
          message: 'Please select atleast one (1) image',
          type: 'error',
        }),
      );
    } else if (
      activeStep === 'Creative' &&
      mediaType === 'video' &&
      (!formValues?.videos?.square || !formValues?.videos?.vertical)
    ) {
      return dispatch(
        toggleAlert({
          toggle: true,
          message: 'Please select atleast one (1) video',
          type: 'error',
        }),
      );
    }

    setActiveStepIndex((prev) => prev + 1);
    setProgress((prevProgress) => prevProgress + (1 / STEPS.length) * 100);
  };

  const handleBack = () => {
    setActiveStepIndex((prev) => prev - 1);
    setProgress((prevProgress) => prevProgress - (1 / STEPS.length) * 100);
  };

  const handleCloseConfirmEdit = () => {
    setConfirmEdit((prev) => !prev);
  };

  const buildPageheader = () => {
    let title: string = '';
    switch (activeStep) {
      case 'Creative':
        title = 'SELECT THE CREATIVE FOR YOUR AD';
        break;
      case 'Details':
        title = 'TEMPLATE DETAILS';
        break;
      default:
        break;
    }

    return title;
  };

  const buildStepsContent = (step: string) => {
    if (step === 'Creative') {
      return (
        <CreativeForm
          brand={brand?.__type === 'location' ? brand?.franchisor : brand}
          accountId={diySettings?.fbAdAccount?.accountId}
          setFormat={setFormat}
          format={format}
          carouselData={carouselData || []}
          setCarouselData={setCarouselData}
          mediaType={mediaType}
          setMediaType={setMediaType}
          assetFeedSpec={mediaForm}
          setAssetFeedSpec={setMediaForm}
          template={formValues}
          setTemplate={setFormValues}
          diySettings={diySettings}
          errors={errors}
          clearErrors={clearErrors}
          register={register}
          isEdit={isEdit || isDuplicate}
          isAdmin={isSuperAdmin || isAdmin}
        />
      );
    }

    if (activeStep === 'Details') {
      return (
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <TextField
                  {...register('name', {
                    required: true,
                  })}
                  fullWidth
                  required
                  size="small"
                  value={formValues?.name}
                  name="name"
                  label="Template Name"
                  onChange={(e: ChangeEventType) => {
                    clearErrors('name');
                    setFormValues({ ...formValues, name: e.target.value });
                  }}
                  variant="standard"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors?.name}
                  helperText={errors?.name && 'Template name is required'}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  {...register('description', {
                    required: true,
                  })}
                  fullWidth
                  required
                  size="small"
                  value={formValues?.description}
                  name="description"
                  label="Description"
                  multiline
                  onChange={(e: ChangeEventType) => {
                    clearErrors('description');
                    setFormValues({
                      ...formValues,
                      description: e.target.value,
                    });
                  }}
                  variant="standard"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors?.description}
                  helperText={
                    errors?.description && 'Template description is required'
                  }
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );
    }

    return (
      <Grid container spacing={1}>
        <Grid
          item
          xs={12}
          sx={{
            justifyContent: 'center',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Spinner />
        </Grid>
      </Grid>
    );
  };

  return (
    <Box className={`${styles.form} ${XsOnly() ? `${styles['-mobile']}` : ''}`}>
      <CircularLoading loading={loading} />

      <Box
        component="div"
        sx={{
          justifyContent: 'space-between',
          display: 'flex',
          alignItems: 'center',
          margin: 0,
          marginBottom: '20px',
        }}
      >
        <Box component="div" sx={{ paddingBottom: 0 }}>
          <PageHeader
            title={buildPageheader()}
            textAlign="left"
            marginBottom="0"
          />
        </Box>
      </Box>

      <Grid container spacing={1} sx={{ minHeight: 'calc(100vh - 222px)' }}>
        <Grid item xs={12}>
          {buildStepsContent(activeStep)}
        </Grid>
      </Grid>

      <Grid
        container
        spacing={1}
        my={4}
        sx={{
          position: 'sticky',
          bottom: 0,
          right: 0,
          left: 0,
          backgroundColor: '#FFF',
          paddingBottom: '20px',
          zIndex: 999,
          alignItems: 'flex-end',
        }}
      >
        <Grid item xs={6}>
          <LinearProgressBarWithLabel value={progress} />
        </Grid>

        <Grid
          item
          sm={6}
          sx={{
            justifyContent: 'flex-end',
            display: 'flex',
          }}
        >
          <PrimaryButton
            title="Cancel"
            type="button"
            handleOnClick={onClose}
            marginRight5
            variant="outlined"
          />

          {activeStepIndex > 0 ? (
            <PrimaryButton
              title="Back"
              type="button"
              handleOnClick={handleBack}
              marginRight5
            />
          ) : null}

          <PrimaryButton
            loading={activeStepIndex + 1 === STEPS.length && loading}
            title={activeStepIndex + 1 === STEPS.length ? 'Finish' : 'Next'}
            type="button"
            handleOnClick={
              activeStepIndex + 1 === STEPS.length
                ? handleSubmit(handleOnSubmit)
                : handleSubmit(handleNext)
            }
          />
        </Grid>
      </Grid>

      <PopupModal open={confirmEdit} handleClose={handleCloseConfirmEdit}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <ModalHeader title="Confirm Edit Template" />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body1" sx={{ textAlign: 'center' }}>
              Modifying this template will affect other ads that use it. Confirm
              edit?
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-line', textAlign: 'center' }}
            >
              {formValues?.adsUsage
                ?.map((ad: CampaignAd) => `${ad.name} (${ad.id})`)
                ?.join('\n')}
            </Typography>
          </Grid>
        </Grid>

        <Grid
          container
          spacing={1}
          sx={{ position: 'sticky', bottom: 0 }}
          mt={2}
        >
          <Grid item xs={12} sx={{ justifyContent: 'center', display: 'flex' }}>
            <PrimaryButton
              title="Cancel"
              type="button"
              handleOnClick={handleCloseConfirmEdit}
              loading={loading}
              marginRight5
            />

            <PrimaryButton
              title="Confirm"
              type="button"
              handleOnClick={handleOnSubmit}
              loading={loading}
            />
          </Grid>
        </Grid>
      </PopupModal>
    </Box>
  );
};

export default TemplateForm;
